import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import {
  EXCEL_ACCEPT,
  analyzeCDR,
  buildNormalizedRowsByCompany,
  detectCompanyByHeaders,
  isSupportedDatasetFile,
  loadDatasetFromFile,
  resolveSelfLabel,
  validateAgainstPattern,
} from "./cdrEngine";
import { buildCrossFileMatches } from "./CDRMultiAnalysis";

export const UP_TO_FIVE_LIMIT = 5;

function validationMessage(validation) {
  const details = [];
  if (validation.missing?.length) details.push(`Missing: ${validation.missing.join(", ")}`);
  if (validation.extra?.length) details.push(`Extra: ${validation.extra.join(", ")}`);
  return details.join(" · ") || "The CDR columns do not match a supported operator format.";
}

export async function parseUpToFiveFile(file) {
  if (!isSupportedDatasetFile(file)) {
    throw new Error("Unsupported file type. Use XLS, XLSX, XLSM, XLSB, or CSV.");
  }

  const dataset = await loadDatasetFromFile(file);
  const headers = Array.isArray(dataset?.headers) ? dataset.headers : [];
  const rows = Array.isArray(dataset?.rows) ? dataset.rows : [];
  const detected = detectCompanyByHeaders(headers);
  const company = detected.detected;
  const validation = company === "Unknown"
    ? { ok: false, missing: [], extra: headers }
    : validateAgainstPattern(headers, company);

  if (!validation.ok) throw new Error(validationMessage(validation));

  const normRows = buildNormalizedRowsByCompany(rows, company);
  if (!normRows.length) throw new Error("No CDR records were found after normalization.");

  const analysis = analyzeCDR(normRows);
  return {
    fileName: file.name,
    company,
    selfLabel: resolveSelfLabel(normRows),
    normRows,
    analysis,
  };
}

export function buildUpToFiveReport(validFiles) {
  const files = validFiles.slice(0, UP_TO_FIVE_LIMIT);
  const matches = files.length > 1
    ? buildCrossFileMatches(files.map((file) => ({ selfLabel: file.selfLabel, analysis: file.analysis })))
    : [];
  const uniqueContacts = new Set();
  const uniqueImeis = new Set();
  const uniqueLocations = new Set();

  files.forEach((file) => {
    (file.analysis.mfcAll || []).forEach((row) => uniqueContacts.add(row.bParty));
    (file.analysis.imeiSummary || []).forEach((row) => uniqueImeis.add(row.imei));
    (file.analysis.mflAll || []).forEach((row) => {
      if (row.location && row.location !== "(blank)") uniqueLocations.add(row.location);
    });
  });

  return {
    files,
    matches,
    totals: {
      files: files.length,
      records: files.reduce((sum, file) => sum + file.normRows.length, 0),
      subscribers: new Set(files.map((file) => file.selfLabel).filter(Boolean)).size,
      contacts: uniqueContacts.size,
      imeis: uniqueImeis.size,
      locations: uniqueLocations.size,
    },
  };
}

export function buildUpToFiveWorkbook(report) {
  if (!report) return null;
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(report.files.map((file) => ({
    File: file.fileName,
    Operator: file.company,
    Subscriber: file.selfLabel,
    Records: file.normRows.length,
    Contacts: file.analysis.mfcAll?.length || 0,
    Locations: file.analysis.mflAll?.length || 0,
    IMEIs: file.analysis.imeiSummary?.length || 0,
  }))), "CDR Summaries");
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(report.matches.map((match) => ({
    Type: match.type,
    Value: match.value,
    Files: match.fileIndexes.map((index) => report.files[index].fileName).join(", "),
  }))), "Cross-file Findings");
  return workbook;
}

function fileSignature(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function SummaryMetric({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 130, flex: "1 1 130px", borderRadius: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function CDRUpToFiveAnalysis() {
  const inputRef = useRef(null);
  const nextIdRef = useRef(0);
  const generationRef = useRef(0);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [report, setReport] = useState(null);

  const validItems = useMemo(() => items.filter((item) => item.status === "ok"), [items]);

  const handleFiles = async (event) => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = "";
    if (!chosen.length) return;

    const existing = new Set(items.map((item) => item.signature));
    const unique = chosen.filter((file) => !existing.has(fileSignature(file)));
    const remaining = Math.max(0, UP_TO_FIVE_LIMIT - items.length);
    const accepted = unique.slice(0, remaining);
    const skipped = chosen.length - accepted.length;

    if (!accepted.length) {
      setNotice(items.length >= UP_TO_FIVE_LIMIT ? "The five-file limit has been reached." : "Duplicate files were skipped.");
      return;
    }

    setNotice(skipped > 0 ? `${skipped} duplicate or over-limit file${skipped === 1 ? " was" : "s were"} skipped.` : "");
    setReport(null);
    setBusy(true);
    const generation = generationRef.current;
    const pending = accepted.map((file) => ({
      id: nextIdRef.current++,
      signature: fileSignature(file),
      file,
      fileName: file.name,
      status: "parsing",
      errorMessage: "",
    }));
    setItems((previous) => [...previous, ...pending]);

    for (const pendingItem of pending) {
      try {
        const parsed = await parseUpToFiveFile(pendingItem.file);
        if (generation !== generationRef.current) return;
        setItems((previous) => previous.map((item) => (
          item.id === pendingItem.id ? { ...item, ...parsed, file: undefined, status: "ok" } : item
        )));
      } catch (error) {
        if (generation !== generationRef.current) return;
        setItems((previous) => previous.map((item) => (
          item.id === pendingItem.id
            ? { ...item, file: undefined, status: "error", errorMessage: error instanceof Error ? error.message : "Unable to parse this CDR." }
            : item
        )));
      }
    }

    if (generation === generationRef.current) setBusy(false);
  };

  const removeItem = (id) => {
    setItems((previous) => previous.filter((item) => item.id !== id));
    setReport(null);
    setNotice("");
  };

  const reset = () => {
    generationRef.current += 1;
    setItems([]);
    setBusy(false);
    setNotice("");
    setReport(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const analyzeBatch = () => {
    setReport(buildUpToFiveReport(validItems));
  };

  const exportReport = () => {
    const workbook = buildUpToFiveWorkbook(report);
    if (workbook) XLSX.writeFile(workbook, `CDR_Up_To_5_Analysis_${Date.now()}.xlsx`);
  };

  return (
    <Box data-testid="up-to-five-workflow">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={EXCEL_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFiles}
      />

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Select one batch containing up to five CDRs. Each file is validated independently, analyzed locally, and included in one consolidated report.
      </Alert>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 2 }}>
        <Button variant="contained" disabled={busy || items.length >= UP_TO_FIVE_LIMIT} onClick={() => inputRef.current?.click()}>
          Select up to 5 CDRs
        </Button>
        <Button variant="outlined" disabled={busy || validItems.length === 0} onClick={analyzeBatch}>
          Analyze CDR batch
        </Button>
        <Button color="inherit" disabled={items.length === 0} onClick={reset}>Reset batch</Button>
        <Chip label={`${items.length} / ${UP_TO_FIVE_LIMIT} files`} variant="outlined" />
        {busy && <Chip icon={<CircularProgress size={14} />} label="Validating files" variant="outlined" />}
      </Stack>

      {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}

      <Stack spacing={1.25}>
        {items.map((item, index) => (
          <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip size="small" label={index + 1} />
              <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{item.fileName}</Typography>
              {item.status === "parsing" && <><CircularProgress size={16} /><Typography variant="caption">Validating…</Typography></>}
              {item.status === "ok" && (
                <>
                  <Chip size="small" color="success" label={item.company} />
                  <Typography variant="caption" color="text.secondary">
                    Subscriber {item.selfLabel} · {item.normRows.length} records
                  </Typography>
                </>
              )}
              {item.status === "error" && <Chip size="small" color="error" label="Invalid CDR" />}
              <Button size="small" color="error" sx={{ ml: { sm: "auto" } }} onClick={() => removeItem(item.id)}>Remove</Button>
            </Box>
            {item.status === "error" && <Alert severity="error" sx={{ mt: 1 }}>{item.errorMessage}</Alert>}
          </Paper>
        ))}
      </Stack>

      {report && (
        <Box sx={{ mt: 3 }} data-testid="up-to-five-report">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 800 }}>Consolidated CDR report</Typography>
              <Typography variant="body2" color="text.secondary">Generated from {report.totals.files} independently validated file{report.totals.files === 1 ? "" : "s"}.</Typography>
            </Box>
            <Button variant="outlined" onClick={exportReport}>Export consolidated Excel</Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", my: 2 }}>
            <SummaryMetric label="Files analyzed" value={report.totals.files} />
            <SummaryMetric label="CDR records" value={report.totals.records} />
            <SummaryMetric label="Subscribers" value={report.totals.subscribers} />
            <SummaryMetric label="Unique contacts" value={report.totals.contacts} />
            <SummaryMetric label="Unique IMEIs" value={report.totals.imeis} />
            <SummaryMetric label="Locations" value={report.totals.locations} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Per-file analysis</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead><TableRow>
                {['File', 'Operator', 'Subscriber', 'Records', 'Contacts', 'Locations', 'IMEIs'].map((label) => <TableCell key={label} sx={{ fontWeight: 700 }}>{label}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {report.files.map((file) => (
                  <TableRow key={file.signature || file.fileName}>
                    <TableCell>{file.fileName}</TableCell><TableCell>{file.company}</TableCell><TableCell>{file.selfLabel}</TableCell>
                    <TableCell>{file.normRows.length}</TableCell><TableCell>{file.analysis.mfcAll?.length || 0}</TableCell>
                    <TableCell>{file.analysis.mflAll?.length || 0}</TableCell><TableCell>{file.analysis.imeiSummary?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Cross-file findings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Shared contacts, direct subscriber contacts, IMEIs, and locations detected across the selected files.
          </Typography>
          {report.matches.length === 0 ? (
            <Alert severity="success">No shared identifiers were found across these CDRs.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 620 }}>
                <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Type</TableCell><TableCell sx={{ fontWeight: 700 }}>Value</TableCell><TableCell sx={{ fontWeight: 700 }}>Files involved</TableCell></TableRow></TableHead>
                <TableBody>{report.matches.map((match, index) => (
                  <TableRow key={`${match.type}-${match.value}-${index}`}>
                    <TableCell>{match.type}</TableCell><TableCell>{match.value}</TableCell>
                    <TableCell>{match.fileIndexes.map((fileIndex) => report.files[fileIndex].fileName).join(", ")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}
